import { LitElement, css, html, nothing, svg, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import sheet from './css/bb-chat-fulfillment.css?inline'
import './bb-address-form'
import type { FulfillmentInputHint } from './bbcore/types'

// Re-exported for back-compat: the address form (and its suggestion
// shape) now live in <bb-address-form>; importers that used to pull
// AddressSuggestion from here still resolve.
export type { AddressSuggestion } from './bb-address-form'

// Inline icons — hidden by CSS in inline (pill) layout, shown in
// stacked (overlay) layout. Stroke uses `currentColor`.
const pickupIcon = svg`
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
       stroke-linejoin="round" aria-hidden="true">
    <path d="M6 7h12l-1 13H7L6 7Z" />
    <path d="M9 7a3 3 0 0 1 6 0" />
  </svg>
`
const deliveryIcon = svg`
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none"
       stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
       stroke-linejoin="round" aria-hidden="true">
    <path d="M3 7h11v9H3z" />
    <path d="M14 10h4l3 3v3h-7" />
    <circle cx="7" cy="18" r="2" />
    <circle cx="17" cy="18" r="2" />
  </svg>
`

// Renders an `input_needed:fulfillment_type` hint as Pickup / Delivery
// buttons. The Delivery sub-form is the shared <bb-address-form>, which
// owns the search/grid/Confirm and emits `set-fulfillment` (plus the
// fetch-* autocomplete events) — all composed, so they bubble up to
// <bb-widget>'s existing handlers. Same form is reused by the
// delivery_address recovery modal.
@customElement('bb-chat-fulfillment')
export class BbChatFulfillment extends LitElement {
  static styles = css`${unsafeCSS(sheet)}`

  @property({ attribute: false }) hint!: FulfillmentInputHint
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) active = true
  // 'inline' (default) pill buttons, or 'stacked' full-width icon-tile
  // rows for the overlay. Reflected so CSS can gate the stacked rules.
  @property({ reflect: true }) layout: 'inline' | 'stacked' = 'inline'

  @state() private deliveryOpen = false

  // Per-button spinner for the Pickup affordance (Delivery's spinner
  // lives inside <bb-address-form>). Cleared on disabled flips / new turn.
  @state() private _busyOpt: 'pick_up' | null = null

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('hint')) {
      this.deliveryOpen = false
      this._busyOpt = null
    }
    if (changed.has('disabled')) this._busyOpt = null
  }

  render() {
    const off = this.disabled
    const stacked = this.layout === 'stacked'
    const showForm =
      this.active && this.deliveryOpen && this.hint.options.includes('delivery')
    return html`
      <div class="affordance" part="affordance">
        ${this.hint.options.map((opt) =>
          opt === 'delivery'
            ? html`<button
                class="opt opt-delivery"
                part="opt opt-delivery"
                ?disabled=${off}
                @click=${() => {
                  if (off) return
                  this.deliveryOpen = !this.deliveryOpen
                  this.emitAffordance('delivery-toggle', { open: this.deliveryOpen })
                }}
              >
                ${stacked
                  ? html`<span class="opt-icon" part="opt-icon" aria-hidden="true">${deliveryIcon}</span>
                      <span class="opt-body" part="opt-body">
                        <span class="opt-label" part="opt-label">Delivery</span>
                        <span class="opt-sub" part="opt-sub">Bring it to my door</span>
                      </span>`
                  : html`Delivery`}
              </button>`
            : html`<button
                class="opt opt-pickup"
                part="opt opt-pickup"
                ?disabled=${off}
                @click=${() => {
                  this._busyOpt = 'pick_up'
                  this.emitAffordance('pickup')
                  this.emitPickup()
                }}
              >
                ${this._busyOpt === 'pick_up'
                  ? html`<span
                        class="bb-btn-spinner"
                        part="btn-spinner"
                        aria-hidden="true"
                      ></span>Confirming…`
                  : stacked
                  ? html`<span class="opt-icon" part="opt-icon" aria-hidden="true">${pickupIcon}</span>
                      <span class="opt-body" part="opt-body">
                        <span class="opt-label" part="opt-label">
                          ${opt === 'pick_up' ? 'Pickup' : opt}
                        </span>
                        <span class="opt-sub" part="opt-sub">I'll come pick it up</span>
                      </span>`
                  : html`${opt === 'pick_up' ? 'Pickup' : opt}`}
              </button>`,
        )}
      </div>
      ${showForm
        ? html`<bb-address-form
            part="address-form"
            .disabled=${off}
            .resetKey=${this.hint.id}
          ></bb-address-form>`
        : nothing}
    `
  }

  private emitPickup() {
    if (this.disabled) return
    this.dispatchEvent(
      new CustomEvent('set-fulfillment', {
        detail: { fulfillmentType: 'pick_up' },
        bubbles: true,
        composed: true,
      }),
    )
  }

  private emitAffordance(kind: string, context?: Record<string, unknown>) {
    this.dispatchEvent(
      new CustomEvent('widget-affordance', {
        detail: { kind, ...(context ? { context } : {}) },
        bubbles: true,
        composed: true,
      }),
    )
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bb-chat-fulfillment': BbChatFulfillment
  }
}
